import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { MasterService } from '../../../../services/master.service'
import { MessageService } from '../../../../services/message.service'
import { StoreService } from '../../../../services/store.service'
import * as languageLibrary from '../../../../services/language'
import { SecurityService } from 'src/app/services/security.service';
import { Role } from 'src/app/models/role.model';
import { Subscription } from "rxjs";

@Component({
    selector: 'roles-detail',
    templateUrl: './roles-detail.component.html',
    styleUrls: ['./roles-detail.component.scss'],
    standalone: false
})
export class RolesDetailComponent implements OnInit {
    /*
    * variables 
    */

    /** Unique identity of the role whose permissions will be displayed. */
    @Input() role: Role = null

    /** A list of page parents to allow when displaying permissions. */
    @Input() parents: string[] = null

    /** A list of page parents to disregard when displaying permissions. */
    @Input() notParents: string[] = null

    public language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    protected words = languageLibrary.language

    protected loading: boolean = false

    protected permissions: {} = {}

    private navbarWatcher: Subscription | null = null

    get modules() { return Object.keys(this.permissions) }

    constructor(private master: MasterService, private ms: MessageService, private store: StoreService, private security: SecurityService) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes) {
            if (changes.role.currentValue !== null) {
                this.role = changes.role.currentValue
                this.getPermissions()
            }
        }
    }

    ngOnInit() {
       this.navbarWatcher = this.ms.channelComponents$.subscribe(res => {
            switch (res.message) {
                case 'changeLanguage':
                    this.language = res.value
                    break
                case 'setPermissions':
                    break
            }
        })
    }

    ngOnDestroy() {
        if (!this.navbarWatcher) {
            return
        }

        this.navbarWatcher.unsubscribe()
    }
    
    /**
     * 
     */
    private getPermissions() {
        if (this.security.notPermitted('security', 'roles permissions', 'view')) {
            return
        }

        if (!this.role['id'] || (this.role['id'] <= 0)) {
            return
        }

        let parentsParam = ''
        if (this.parents && (this.parents.length > 0)) {
            parentsParam = `parents=${encodeURIComponent(this.parents.join(','))}`
        }

        let notParentsParam = ''
        if (this.notParents && (this.notParents.length > 0)) {
            notParentsParam = `notParents=${encodeURIComponent(this.notParents.join(','))}`
        }

        let query = ''
        if ((parentsParam && (parentsParam.length > 0)) || (notParentsParam && (notParentsParam.length > 0))) {
            query = '?'
        }

        this.loading = true

        this.master.get(`roles/${this.role['id']}${query}${parentsParam}${notParentsParam}`, res => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.permissions = []
            for (const permission of res.data.pages) {
                if (this.parents && (this.parents.length > 0) && (!this.parents.includes(permission.parent.toLowerCase()))) {
                    continue
                }

                if (this.notParents && (this.notParents.length > 0) && (this.notParents.includes(permission.parent.toLowerCase()))) {
                    continue
                }

                const module = permission.module;
                if (!this.permissions[module]) {
                    this.permissions[module] = [];
                }

                this.permissions[module].push(permission);
            }
        })
    }

    /**
     * A single checkbox was checked by the user.
     * @param permission 
     * @param attribute 
     * @returns 
     */
    protected modifySinglePermission(permission, attribute) {
        if (this.security.notPermitted('security', 'roles permissions', 'update')) {
            return
        }
        
        if (!permission['id'] || (permission['id'] <= 0)) {
            return
        }

        
        const item = document.getElementById(`${attribute}_${permission['id']}`) as HTMLInputElement
        if (!item) {
            return
        }

        const roleId = parseInt(permission['roleId'])
        const pageId = parseInt(permission['id'])

        const payload = {
            permissions: [{
                pageId: pageId,
                [attribute]: item.checked
            }]
        }

        this.modify(roleId, payload)
    }

    /**
     * A page row was clicked by the user so set all of those permissions (view, add, update, delete) to true.
     * @param pageId Unique internal identity of the page whose permission row was selected.
     */
    protected modifyPagePermissions(pageId: number) {
        if (this.security.notPermitted('security', 'roles permissions', 'update')) {
            return
        }

        if (!pageId || isNaN(pageId) || (pageId <= 0)) {
            return
        }

        const payload = {
            permissions: [{
                pageId: pageId
            }]
        }

        const view = document.querySelector(`input#view_${pageId}`) as HTMLInputElement
        if (view) {
            view.checked = !view.checked
            payload.permissions[0]['view'] = view.checked
        }

        const add = document.querySelector(`input#add_${pageId}`) as HTMLInputElement
        if (add) {
            add.checked = !add.checked
            payload.permissions[0]['add'] = add.checked
        }

        const modify = document.querySelector(`input#modify_${pageId}`) as HTMLInputElement
        if (modify) {
            modify.checked = !modify.checked
            payload.permissions[0]['modify'] = modify.checked
        }

        const discard = document.querySelector(`input#delete_${pageId}`) as HTMLInputElement
        if (discard) {
            discard.checked = !discard.checked
            payload.permissions[0]['delete'] = discard.checked
        }

        if (!view && !add && !modify && !discard) { // no inputs associated with this page (header?)?
            return
        }

        this.modify(this.role.id, payload)
    }

    /**
     * Modify all permissions based on the column clicked.
     * @param {Event} e
     * @param {string} column The name of the column permission (view, add, modify or delete) that was clicked by the user.
     */
    protected modifyColumnPermissions(e: Event, column: string) {
        e.stopPropagation()
        e.preventDefault()

        if (this.security.notPermitted('security', 'roles permissions', 'update')) {
            return
        }

        if (!column || (column.length <= 0) || (!this.permissions)) {
            return
        }

        const payload = {
            permissions: []
        }

        const modules = Object.keys(this.permissions)
        if (!modules || (modules.length <= 0)) {
            return
        }

        for (const module of modules) {
            const pages = this.permissions[module]
            if (!pages || (pages.length <= 0)) {
                continue
            }

            for (const page of pages) {
                if (!page.indent) { // skip indent = 0
                    continue
                }

                const input = document.querySelector(`input#${column}_${page.id}`) as HTMLInputElement
                if (!input) {
                    continue
                }

                input.checked = !input.checked

                const permission = {
                    pageId: page.id,
                    [column]: input.checked
                }

                payload.permissions.push(permission)
            }
        }

        this.modify(this.role.id, payload)
    }

    /**
     * 
     * @param e 
     * @param module 
     */
    protected modifyModulePermissions = (e: Event, module: string) => {
        e.stopPropagation()
        e.preventDefault()

        if (!module || (module.length <= 0) || !this.permissions) {
            return
        }

        const pages = this.permissions[module]
        if (!pages || (pages.length <= 0)) {
            return
        }

        const payload = {
            permissions: []
        }

        for (const page of pages) {
            const permission = {
                pageId: page.id
            }

            const view = document.querySelector(`input#view_${page.id}`) as HTMLInputElement
            if (view) {
                view.checked = !view.checked
                permission['view'] = view.checked
            }

            const add = document.querySelector(`input#add_${page.id}`) as HTMLInputElement
            if (add) {
                add.checked = !add.checked
                permission['add'] = add.checked
            }

            const modify = document.querySelector(`input#modify_${page.id}`) as HTMLInputElement
            if (modify) {
                modify.checked = !modify.checked
                permission['modify'] = modify.checked
            }

            const discard = document.querySelector(`input#delete_${page.id}`) as HTMLInputElement
            if (discard) {
                discard.checked = !discard.checked
                permission['delete'] = discard.checked
            }

            if (!view && !add && !modify && !discard) { // no inputs associated with this page (header?)?
                continue
            }

            payload.permissions.push(permission)
        }

        this.modify(this.role.id, payload)
    }

    /**
     * Modify the given permissions for the given role.
     * @param roleId 
     * @param permissions 
     * @returns 
     */
    private modify = (roleId: number, permissions) => {
        if (!roleId || (roleId <= 0) || !permissions) {
            return
        }

        this.loading = true
        this.master.patch(`roles/${roleId}/permissions`, permissions, res => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

        })
    }
}
